import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaBoxOpen, FaShieldAlt, FaTruck, FaUserCheck } from 'react-icons/fa';
import Hero from '../components/Hero';
import heroProgetto from '../assets/heroproduttori.webp';
import { trackPartnerEvent } from '../hooks/useCandidaturaAnalytics';

const FAQ = [
  {
    q: 'Quanto costa candidarsi?',
    a: 'Nulla. Nessuna quota di affiliazione, nessuna commissione sulle vendite.',
  },
  {
    q: 'Quanto tempo serve per iniziare?',
    a: 'Bastano 2 minuti per salvare una bozza e ricevere il codice accesso via email. Puoi completare i dati quando vuoi.',
  },
  {
    q: 'Cosa succede dopo l\'invio?',
    a: 'Il team valuta la candidatura. Se approvata, spedisci un campione e ricevi credenziali per l\'area riservata.',
  },
  {
    q: 'Posso candidarmi per più prodotti?',
    a: 'Sì, puoi proporre più prodotti nella stessa candidatura, anche di categorie diverse.',
  },
];

const TIMELINE = [
  { icon: <FaBoxOpen />, title: 'Compila i dati', desc: 'Azienda, referenti e categoria' },
  { icon: <FaUserCheck />, title: 'Aggiungi prodotti', desc: 'Schede, foto e capacità produttiva' },
  { icon: <FaShieldAlt />, title: 'Valutazione', desc: 'Review qualità e campione' },
  { icon: <FaTruck />, title: 'Vai online', desc: 'Catalogo, ordini e pagamenti' },
];

export default function DiventaPartner() {
  const navigate = useNavigate();
  const [ruolo, setRuolo] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  const startCandidatura = (selectedRuolo) => {
    trackPartnerEvent('partner_cta_click', { source: 'diventa_partner', ruolo: selectedRuolo });
    const params = new URLSearchParams({ ruolo: selectedRuolo });
    if (selectedRuolo === 'fornitore') params.set('categoria', 'Logistica');
    navigate(`/candidatura?${params.toString()}`);
  };

  return (
    <div className="bg-[#FFFEFA] min-h-screen pb-32">
      <Hero src={heroProgetto} />

      <div className="max-w-4xl mx-auto -mt-12 z-10 relative px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center"
        >
          <h1 className="text-2xl sm:text-4xl font-bold text-[#c78244] mb-2">
            Diventa partner Mangitalia
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Gratuito · Pagamento alla consegna · Catalogo curato · Kit di avvio per produttori selezionati
          </p>
        </motion.div>
      </div>

      <section className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="text-xl font-bold text-center text-gray-800 mb-6">Scegli il tuo percorso</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            onClick={() => setRuolo('produttore')}
            className={`text-left p-6 rounded-2xl border-2 transition-all ${
              ruolo === 'produttore' ? 'border-[#c78244] bg-[#fff8ea] shadow-lg' : 'border-gray-200 bg-white hover:border-[#c78244]/50'
            }`}
          >
            <span className="text-3xl mb-3 block">🧀</span>
            <h3 className="font-bold text-lg text-gray-800 mb-2">Produttore artigianale</h3>
            <p className="text-sm text-gray-600">
              Formaggi, olio, salumi, forno, conserve e specialità territoriali da vendere nel catalogo.
            </p>
          </motion.button>

          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            onClick={() => setRuolo('fornitore')}
            className={`text-left p-6 rounded-2xl border-2 transition-all ${
              ruolo === 'fornitore' ? 'border-[#c78244] bg-[#fff8ea] shadow-lg' : 'border-gray-200 bg-white hover:border-[#c78244]/50'
            }`}
          >
            <span className="text-3xl mb-3 block">🚚</span>
            <h3 className="font-bold text-lg text-gray-800 mb-2">Partner logistica e servizi</h3>
            <p className="text-sm text-gray-600">
              Trasporti, catena del freddo, packaging, integrazioni software e consulenza digitale.
            </p>
          </motion.button>
        </div>

        {ruolo && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 flex flex-wrap justify-center gap-3"
          >
            <button
              type="button"
              onClick={() => startCandidatura(ruolo)}
              className="bg-gradient-to-r from-[#F6CF48] to-[#c78244] text-white px-8 py-3 rounded-full font-bold shadow-lg"
            >
              Inizia in 2 minuti
            </button>
            <Link
              to="/presentazione"
              onClick={() => trackPartnerEvent('partner_cta_click', { source: 'diventa_partner_presentazione' })}
              className="border border-[#c78244] text-[#c78244] px-8 py-3 rounded-full font-semibold"
            >
              Scopri i vantaggi
            </Link>
            <Link
              to="/per-partner"
              onClick={() => trackPartnerEvent('partner_cta_click', { source: 'diventa_partner_brochure' })}
              className="border border-gray-300 text-gray-700 px-8 py-3 rounded-full font-semibold"
            >
              FAQ modello business
            </Link>
          </motion.div>
        )}
      </section>

      <section className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
          {TIMELINE.map((t, i) => (
            <div key={t.title} className="bg-white rounded-xl p-4 shadow text-center">
              <div className="text-[#c78244] text-2xl mb-2 flex justify-center">{t.icon}</div>
              <p className="text-xs text-gray-400 mb-1">Step {i + 1}</p>
              <h3 className="font-bold text-sm text-gray-800">{t.title}</h3>
              <p className="text-xs text-gray-500 mt-1">{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-4 py-8">
        <h2 className="text-xl font-bold text-center text-[#c78244] mb-6">Domande frequenti</h2>
        <div className="space-y-2">
          {FAQ.map((item, i) => (
            <div key={item.q} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full text-left px-4 py-3 font-semibold text-gray-800 flex justify-between"
              >
                {item.q}
                <span>{openFaq === i ? '−' : '+'}</span>
              </button>
              {openFaq === i && (
                <p className="px-4 pb-3 text-sm text-gray-600">{item.a}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      <div className="fixed bottom-0 left-0 w-full z-50 bg-[#FFFEFA]/95 backdrop-blur flex flex-col items-center py-4 shadow-[0_-2px_16px_rgba(199,130,68,0.12)]">
        <button
          type="button"
          onClick={() => startCandidatura(ruolo || 'produttore')}
          className="bg-gradient-to-r from-[#F6CF48] to-[#c78244] text-white px-8 py-3 rounded-full shadow-2xl text-lg font-bold"
        >
          {ruolo ? 'Inizia la candidatura' : 'Candidati gratis'}
        </button>
        <Link to="/candidatura?recupera=1" className="mt-2 text-sm text-[#c78244] underline">
          Hai già un codice di accesso?
        </Link>
      </div>
    </div>
  );
}
